import React, { useState, useMemo, useContext }  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, Spinner, useTheme } from '@ui-kitten/components'
import { FontAwesome5 } from '@expo/vector-icons'
import { useColors } from '../../utils/useColors'
import { HintView } from '../../components/HintView'
import { saveFiscalOperation, setNewLKTokens } from '../../global/lightkassa'
import { saveSale, getAccessToken, isCashboxBusy, cashboxQueueKey } from '../../global/wipon'
import { mirrorSaleFiscalInfo, updateSale, updateStoreWiponCredentials } from '../../global/Firebase/firebase'
import { validateDailyStats } from '../../utils/validationFunctions'
import { currencyFormatterAtom, storesDictAtom, usersDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import OperationsList from '../../components/OperationsList'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { getFiscalCredentials, getFiscalOperatorId, isFiscalizationEnabled } from '../../utils/fiscalization'
import { UserContext } from '../../global/UserProvider'
import { forgetResolvedSales, unfiscalizedSaleKey } from '../../utils/unfiscalizedSales'
import { readMasterFiscalState } from '../../utils/fiscalRetryGuard'

export const cartItemNames = (cartItems, nameField='name', quantityField='quantity', separator=', ') => {
  if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
    return ''
  }
  var names = ''
  for (const [index, value] of cartItems.entries()) {
    if (!value) continue
    const name = value[nameField] || value.product?.name || 'Unknown'
    const quantity = value[quantityField] || 0
    names = names + separator + name + ' (' + (Number.isInteger(quantity) ? quantity : quantity.toFixed(2)) + ')'
  }
  return names.slice(separator.length)
}

export const renderSalesItemFieldsToShow = (item, date) => {
  const { t } = useTranslation()
  const colors = useColors()
  const theme = useTheme()
  const usersDict = useAtomValue(usersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  return (
    <>
      <View style={styles.dateTimeView}>
        <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
        <Text
          style={styles.itemTitle}
          category='s2'>
          {date}
        </Text>
        { item.paymentMethod && (item.paymentMethod?.id || typeof item.paymentMethod === 'string') && ( // old sales did not have payment id
          <FontAwesome5 name={item.paymentMethod?.icon || item.paymentMethod} size={16} color={theme['text-basic-color']} style={styles.icon}/>
        )}
        { item.secondPaymentMethod && (
          <FontAwesome5 name={item.secondPaymentMethod?.icon || item.secondPaymentMethod} size={16} color={theme['text-basic-color']} style={styles.icon}/>
        )}
        { item.paymentMethod && !item.paymentMethod?.id && typeof item.paymentMethod !== 'string' && (
          <FontAwesome5 name='ban' size={16} color={colors.red} style={styles.icon}/>
        )}
        <Text
          style={styles.itemTitle}
          category='s2'>
          {currencyFormatter.format(item.finalTotalCartPrice || item.totalCartPrice || item.totalPrice || 0)}
        </Text>
      </View>
      <View style={styles.dateTimeView}>
        { item.purchaseMethod?.id ? (
          <FontAwesome5 name={item.purchaseMethod?.icon || item.purchaseMethod} size={14} color={theme['text-basic-color']} style={styles.icon}/>
        ) : ( // to support old
          <>
            {(item.purchaseMethod == 0 || item.purchaseMethod === '0') && (<FontAwesome5 name="building" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
            {(item.purchaseMethod == 1 || item.purchaseMethod === '1') && (<FontAwesome5 name="walking" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
            {(item.purchaseMethod == 2 || item.purchaseMethod === '2') && (<FontAwesome5 name="taxi" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
          </>
        ) }
        <Text
          style={styles.itemTitle}
          category='s2'>
          {storesDict?.[item.store]?.name}
        </Text>
        <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
        <Text
          style={styles.itemTitle}
          category='s2'>
          {usersDict[item.cashierUID || item.cashierId]?.name || 'Unknown'}
        </Text>
      </View>
      {isFiscalizationEnabled(storesDict?.[item.store || item.storeId]) && !item.fiscalInfo && item.paymentMethod && (
        <Text status='danger'>
          {t('salesHistory.notFiscalized')}
        </Text>
      )}
      {item.returnInfo && (
        <Text status='danger'>
          {t('salesHistory.returnFiled')}
        </Text>
      )}
      {item.client && (
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <Icon name='heart' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{(item.client.phoneNumber || '') + ' ' + (item.client.displayName || '')}</Text>
        </View>
      )}
      {item.cartItems ? (
        <Text>
          {cartItemNames(item.cartItems)}
        </Text>
      ) : null}
    </>
  )
}

export default function SalesHistoryScreen({ navigation, initialRange=null }) {
  const { t } = useTranslation()
  // ATL-1020: the user context was only read to key fiscal retries off the CASHIER's store,
  // which was the defect fixed in `retryFiscalOnSales` below. Nothing on this screen needs it now.
  const usersDict = useAtomValue(usersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const [salesWithoutFiscal, setSalesWithoutFiscal] = useState([])
  const [, setContext] = useContext(UserContext)
  const [fiscalLoading, setFiscalLoading] = useState(false)

  let additionalDataValidation = (data, localStoreRange, store) => {
    validateFiscalInfo(data)
    let allDatesSales = 0
    let allDatesQuantity = 0
    const employeesTotals = {}
    for (let item of data) {
      // let date = parse(item.title, "d/M/y EEEE", new Date())
      // validateDailyStats(date, store, true, {sales: true}, {sales: item.data})
      let totalSales = 0
      let totalQuantity = 0
      for (let sale of item.data) {
        totalSales += (sale.finalTotalCartPrice || sale.totalCartPrice || sale.totalPrice || 0)
        totalQuantity += 1

        const employeeId = sale.cashierUID || sale.cashierId || sale.createdBy
        const employeeName = usersDict?.[employeeId]?.name || employeeId || 'Unknown'

        if (!employeesTotals[employeeName]) {
          employeesTotals[employeeName] = {
            complex: { totalSales: 0, totalQuantity: 0 },
            regular: { totalSales: 0, totalQuantity: 0 },
          }
        }

        employeesTotals[employeeName].regular.totalSales += Number(sale.finalTotalCartPrice || sale.totalCartPrice || sale.totalPrice || 0)
        employeesTotals[employeeName].regular.totalQuantity += 1
      }
      console.log(totalSales, totalQuantity)
      allDatesQuantity += totalQuantity
      allDatesSales += totalSales
    }
    console.log(allDatesQuantity, allDatesSales)
    Object.entries(employeesTotals).forEach(([name, totals]) => {
      if (totals.complex.totalQuantity > 0) {
        console.log(name, 'complex', totals.complex.totalSales, totals.complex.totalQuantity)
      }
      if (totals.regular.totalQuantity > 0) {
        console.log(name, 'regular', totals.regular.totalSales, totals.regular.totalQuantity)
      }
    })
  }

  let validateFiscalInfo = (data) => {
    let newSalesWithoutFiscal = []
    for (let section of data) {
      if (section.data && Array.isArray(section.data)) {
        for (let item of section.data) {
          const storeId = item.store || item.storeId
          const paymentMethod = item.paymentMethod
          if (isFiscalizationEnabled(storesDict?.[storeId]) && !item.fiscalInfo && paymentMethod) {
            newSalesWithoutFiscal.push(item)
          }
        }
      }
    }
    setSalesWithoutFiscal(newSalesWithoutFiscal)
  }

  // Export transformer for sales data
  const exportSalesDataTransformer = useMemo(() => {
    return (items, t) => {
      return items.map(sale => {
        // Get the sale date
        let saleDate = ''
        const dateValue = sale.saleDate
        if (dateValue) {
          if (dateValue.toDate) {
            saleDate = format(getLocalStoreDate(sale.store, dateValue.toDate()), 'd/M/y HH:mm')
          } else if (dateValue instanceof Date) {
            saleDate = format(getLocalStoreDate(sale.store, dateValue), 'd/M/y HH:mm')
          } else if (typeof dateValue === 'string') {
            saleDate = format(getLocalStoreDate(sale.store, new Date(dateValue)), 'd/M/y HH:mm')
          }
        }

        // Get payment method name
        const paymentMethod = sale.paymentMethod
        const paymentMethodName = paymentMethod?.name || (typeof paymentMethod === 'string' ? paymentMethod : '')

        // Get purchase method name
        const purchaseMethod = sale.purchaseMethod
        const purchaseMethodName = purchaseMethod?.name || (typeof purchaseMethod === 'string' ? purchaseMethod : '')

        // Get cashier name
        const cashierId = sale.cashierUID || sale.cashierId
        const cashierName = usersDict?.[cashierId]?.name || cashierId || ''

        // Get store name
        const storeId = sale.store || sale.storeId
        const storeName = storesDict?.[storeId]?.name || storeId || ''

        // Get client info
        const clientInfo = sale.client ? `${sale.client.phoneNumber || ''} ${sale.client.displayName || ''}`.trim() : ''

        // Get cart items
        const cartItems = sale.cartItems || []
        // '; ' between items keeps a product's own commas ("Пупырка 0,6м") intact (ATL-1899).
        const cartItemsStr = cartItemNames(cartItems, 'name', 'quantity', '; ')

        return {
          [t('date')]: saleDate,
          [t('store')]: storeName,
          [t('salesHistory.total')]: sale.finalTotalCartPrice || sale.totalCartPrice || sale.totalPrice || 0,
          [t('salesHistory.payment')]: paymentMethodName,
          [t('salesHistory.priceType')]: purchaseMethodName,
          [t('salesHistory.cashier')]: cashierName,
          [t('salesHistory.client')]: clientInfo,
          [t('products')]: cartItemsStr,
          [t('salesHistory.notFiscalized')]: sale.fiscalInfo ? t('no') : t('yes'),
          [t('salesHistory.returnFiled')]: sale.returnInfo ? t('yes') : t('no'),
        }
      })
    }
  }, [usersDict, storesDict])

  /**
   * ATL-1020 — a receipt is re-fiscalized on the register that ISSUED it.
   *
   * `validateFiscalInfo` above collects unfiscalized sales from every store in the loaded range
   * (`OperationsList` starts on `userStoreId || 'all'`, and any user can switch the filter to
   * "all"), but this handler read the operator, the credentials AND the Light Kassa token store
   * from `context.user.store?.id` — the cashier's CURRENT store. An admin standing in branch B
   * therefore sent branch A's sales to B's OFD and then wrote B's fiscal sign onto A's sale, with
   * no failure recorded. That is the same wrong-store defect this ticket fixed on the delete
   * button; the batch retry is the other half of it.
   *
   * ─── WHY THE TWO OPERATORS PREPARE DIFFERENTLY ─────────────────────────────────────────────
   * The two integrations hold their token in different places, and a batch that spans stores is
   * the first thing that ever made the difference matter:
   *
   *  • Light Kassa keeps ONE module-global `accessToken` (global/lightkassa.js) that
   *    `saveFiscalOperation` reads directly — the credentials argument only carries the `pin`. So
   *    the global has to be re-pointed at the store whose receipt is being signed. Refreshing
   *    "once per distinct store" is wrong here: after a second Light Kassa store is prepared, the
   *    global points at IT, and the next sale from the first store would be signed with the second
   *    store's token. It is therefore re-pointed whenever the store changes — once for the
   *    ordinary single-store batch, and again on every switch. This mirrors what `getOfdStats`
   *    (Shift) and `deleteSale` already do before every Light Kassa call.
   *
   *  • Wipon takes the token from the credentials OBJECT (`credentials.accessToken`), so the
   *    refreshed credentials must be the ones handed to `saveSale` — reading `wiponCredentials`
   *    back off the store would re-send the expired token this retry exists to replace, since
   *    `storesDict` cannot have observed the write yet. Cached per store so the login happens once.
   */
  const retryFiscalOnSales = async () => {
    setFiscalLoading(true)
    /**
     * The sales that are STILL unsigned when this pass ends — the ones that failed, the ones no
     * operator could sign, and (on the busy break below) the ones never attempted.
     *
     * ATL-1237 review: this list is written back over `salesWithoutFiscal`. It used to be dropped
     * on the floor unless every single sale signed, which left an already-signed sale sitting in
     * the banner — and pressing the button again re-POSTed a ticket for a sale that already has
     * `fiscalInfo`, i.e. a SECOND fiscal document for one sale on the merchant's register. The
     * endpoint takes no idempotency key, so nothing downstream could collapse it. The busy break
     * makes a partial pass the normal outcome and explicitly invites a second press, so the
     * narrowing has to happen.
     */
    let failedSales = []
    /** The store the Light Kassa module globals currently point at. */
    let lkTokensPointAt = null
    const prepareLKTokens = async (storeId, store) => {
      if (lkTokensPointAt === storeId) return
      await setNewLKTokens(getFiscalCredentials(store), storeId)
      lkTokensPointAt = storeId
    }
    /** storeId → the credentials carrying THIS store's freshly issued access token. */
    const wiponCredentialsByStore = new Map()
    const prepareWiponCredentials = async (storeId, store) => {
      if (wiponCredentialsByStore.has(storeId)) return wiponCredentialsByStore.get(storeId)
      let credentials = getFiscalCredentials(store)
      const token = await getAccessToken(credentials)
      if (token) {
        credentials = {...credentials, accessToken: token}
        try {
          // Persisting the token only saves the NEXT retry a login — the fiscalization below uses
          // the in-memory copy. So a failed write must not abort the batch, but it must not be an
          // unhandled rejection either: this used to be a floating promise.
          await updateStoreWiponCredentials(store?.ref, credentials)
        } catch (error) {
          console.warn('[retryFiscalOnSales] could not persist refreshed wipon credentials', error)
        }
      }
      wiponCredentialsByStore.set(storeId, credentials)
      return credentials
    }
    /**
     * The sales this pass actually signed — or found ALREADY signed at the write master — the only
     * ones it is safe to REMOVE from the banner.
     *
     * ATL-1237 review 3 follow-up. The `catch` below deliberately leaves the banner alone, because
     * narrowing it to `failedSales` there would drop the sales the throw never reached and make
     * genuinely unsigned sales invisible. But leaving it *whole* re-creates the duplicate this
     * branch exists to prevent: a sale signed before the throw stays listed, and the next press
     * re-POSTs a ticket for a sale that already carries `fiscalInfo`. Tracking what was signed
     * lets the throw path remove exactly that and nothing else, instead of depending on the
     * Firestore listener having refreshed first.
     */
    const signedSales = new Set()
    /** The sales whose master could not be read this pass — left unsigned and in the banner. */
    const unverifiableSales = []

    /**
     * The cashboxes that answered «Касса в обработке» during THIS pass, keyed `profile:cashbox`.
     *
     * ATL-1237 review 3. `saveSale` now spends up to ~38s of backoff on a busy cashbox and this
     * loop is sequential behind a full-screen spinner with no cancel, so not carrying on against
     * the SAME cashbox is right — it is busy for the next ticket too. But this used to `break` the
     * whole loop, which also abandoned every later sale belonging to a DIFFERENT cashbox and to
     * Light Kassa, neither of which has anything to do with it. The batch is collected from every
     * store in the loaded range (see ATL-1020 above), so an admin on the "all" filter lost the
     * entire tail to one busy register and had to press the button once per remaining store. The
     * queue in `global/wipon.js` is keyed per cashbox for exactly this reason; so is this.
     */
    const busyCashboxes = new Set()
    try {
      for (const sale of salesWithoutFiscal) {
        // The banner was built from `!sale.fiscalInfo` on whatever this screen loaded, which for a
        // Supabase-read organization is the mirror — and the mirror lags the Firestore write
        // master (another till may have signed this sale with its PATCH still in its outbox).
        // Signing again is a SECOND receipt, so ask the master first (utils/fiscalRetryGuard).
        const master = await readMasterFiscalState(sale)
        if (master.kind === 'fiscalized') {
          // Already signed: take it off the banner and the register, and repair the mirror whose
          // missing receipt put it in the banner (Supabase only — Firestore already has it).
          signedSales.add(sale)
          try {
            await mirrorSaleFiscalInfo(sale, master.fiscalInfo)
          } catch (error) {
            console.warn('[retryFiscalOnSales] could not re-mirror an already-signed receipt', error)
          }
          continue
        }
        if (master.kind === 'unknown') {
          // Not verifiable (offline, no master document): not attempted, so it stays unsigned and
          // stays in the banner. Never signed on a guess.
          unverifiableSales.push(sale)
          failedSales.push(sale)
          continue
        }
        const storeId = sale.store || sale.storeId
        const store = storesDict?.[storeId]
        const operatorId = getFiscalOperatorId(store)
        if (operatorId === 'light-kassa') {
          await prepareLKTokens(storeId, store)
          let fiscalInfo = await saveFiscalOperation(getFiscalCredentials(store), sale, storeId)
          if (!fiscalInfo) {
            failedSales.push(sale)
          } else {
            // Recorded BEFORE the Firestore write, because what this set stands for is "the OFD
            // has already signed this sale" — which is true the moment `fiscalInfo` came back,
            // whether or not `updateSale` then lands. If the write throws (an offline till), the
            // receipt exists at the operator with nothing in Firestore to show for it; adding
            // after the await would leave that sale in the banner and let the next press punch a
            // SECOND fiscal document for it, the exact duplicate this set exists to prevent.
            signedSales.add(sale)
            await updateSale(sale, {fiscalInfo})
          }
        } else if (operatorId === 'wipon-kassa') {
          const credentials = await prepareWiponCredentials(storeId, store)
          const cashboxKey = cashboxQueueKey(credentials)
          if (busyCashboxes.has(cashboxKey)) {
            // This register already refused a ticket as busy in this pass, so it would refuse this
            // one too — after another ~38s of backoff. Never attempted, therefore still unsigned,
            // therefore it stays in the banner for the next press.
            failedSales.push(sale)
            continue
          }
          let lastError = null
          let fiscalInfo = await saveSale(credentials, sale, (error) => { lastError = error })
          if (!fiscalInfo) {
            failedSales.push(sale)
            // Stop sending to THIS cashbox for the rest of the pass — see `busyCashboxes` above.
            if (isCashboxBusy(lastError)) busyCashboxes.add(cashboxKey)
          } else {
            // Before the write — see the Light Kassa branch above.
            signedSales.add(sale)
            await updateSale(sale, {fiscalInfo})
          }
        } else {
          // A store configured to an operator this app cannot talk to. `isFiscalizationEnabled`
          // deliberately still counts it as fiscalizing, so the sale IS in the banner — and nothing
          // signed it here. Reporting success and clearing the banner would tell the cashier a
          // receipt was fiscalized when no OFD was ever contacted.
          failedSales.push(sale)
        }
      }
      // ATL-1237 review: narrow the banner to what is STILL unsigned instead of leaving it whole
      // on a partial pass. Leaving an already-signed sale in it meant a second press re-POSTed a
      // ticket for a sale that already carries `fiscalInfo` — a duplicate fiscal document, which
      // no idempotency key exists to collapse.
      setSalesWithoutFiscal(failedSales)
      // Two different failures, two different remedies: the OFD refused (retry later / check the
      // register), or the master could not be read (check the connection). A mixed pass says both.
      const ofdFailures = failedSales.length - unverifiableSales.length
      const problems = [
        ...(ofdFailures > 0 ? [t('salesHistory.ofdError')] : []),
        ...(unverifiableSales.length > 0 ? [t('salesHistory.fiscalStateUnverifiable')] : []),
      ]
      alert(problems.length > 0 ? problems.join('\n') : t('success'))
    } catch (error) {
      // ATL-1237 review 3: the `finally` below cleared the spinner, but with no `catch` a throw
      // still skipped `setSalesWithoutFiscal` AND both alerts, and escaped `retryFiscalOnSales`
      // as an unhandled rejection — `action` is invoked from `onPress`, which discards the
      // returned promise. The cashier was left with a dismissed spinner and no message at all,
      // which reads exactly like success. The banner is NOT rebuilt from `failedSales` here — the
      // throw may have happened before most sales were reached, and those are still unsigned.
      // Only the sales this pass VERIFIABLY signed are removed: that keeps every still-unsigned
      // sale visible while leaving no signed one for the next press to re-POST.
      console.warn('[retryFiscalOnSales] batch fiscalization aborted', error)
      if (signedSales.size > 0) {
        setSalesWithoutFiscal(salesWithoutFiscal.filter((sale) => !signedSales.has(sale)))
      }
      alert(t('salesHistory.ofdError'))
    } finally {
      // ATL-1274 — New Sale keeps a till-local register of the sales IT failed to fiscalize, and
      // shows a persistent banner for them in place of the blocking alert it used to raise per
      // sale. Anything this pass signed is no longer one of them. Pruned in the `finally` and
      // keyed on `signedSales` for the same reason that set exists at all: it is the only list
      // that is true on BOTH the clean path and the throw path, and pruning anything wider would
      // tell the cashier a sale was signed when nothing signed it.
      if (signedSales.size > 0) {
        const signedIds = [...signedSales].map(unfiscalizedSaleKey)
        setContext(ctx => ({...ctx, unfiscalizedSales: forgetResolvedSales(ctx.unfiscalizedSales, signedIds)}))
      }
      // The spinner is full-screen and has no cancel: a throw anywhere above (a Firestore
      // `updateSale` that rejects, a store whose credentials are malformed) used to strand the
      // cashier on it with no way back to the sales list.
      setFiscalLoading(false)
    }
  }
  
  return (
    <View style={styles.container}>
      { salesWithoutFiscal.length > 0 && (
        fiscalLoading ? (
          <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}><Spinner size='giant'/></View>
        ) : (
          <HintView 
            text={t('salesHistory.periodContainsNotFiscalizedSales')}
            iconName='alert-circle-outline' 
            action={retryFiscalOnSales}
            callToAction={t('salesHistory.batchFiscalizationRetry')}
            style={{marginVertical: 10, marginHorizontal: 20}}
          />
        )
      )}
      <OperationsList collectionName='sales' renderItemFieldsToShow={renderSalesItemFieldsToShow}
        navigation={navigation} dateField={'saleDate'} additionalFilters={null}
        detailScreenName={'SaleDetail'} topView={() => {return null}} initialRange={initialRange}
        showTotalSum={false} totalSumFormatter={currencyFormatter} totalSumField={'finalTotalCartPrice'}
        showAddButton={false} additionalDataValidation={additionalDataValidation} detailScreenParams={{}}
        showStoreFilter={true}
        showExportButtons={true}
        exportDataTransformer={exportSalesDataTransformer}
        exportFilenamePrefix='sales-history'
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
})
